import { openDB } from 'idb';
import type { DBSchema, IDBPDatabase } from 'idb';
import type {
  Appointment,
  Blackout,
  ClientDetail,
  Hours,
  Resource,
  Service,
  Session,
} from '../wire';
import type { OutboxEntry } from '../outbox/entry';

/**
 * The device's two halves, in one IndexedDB database: **the mirror and the
 * outbox** (spec §8).
 *
 * They are in one database on purpose. An intent settles and a mirrored row
 * changes as a consequence, and the two writes want to be one transaction —
 * IndexedDB will only give that across stores of the same database. Splitting
 * them would let the device restart between "the booking landed" and "the intent
 * is done", which is the one interleaving that could show an appointment twice.
 *
 * `appointments` is indexed `by-day` — the ISO date in the **tenant's** timezone,
 * computed on write. Today's book is a range query on an index rather than a
 * full scan with a timezone conversion per row, and the day boundary is the
 * tenant's rather than the iPad's, which matters the moment a device travels.
 */

export interface MirroredAppointment extends Appointment {
  /** `YYYY-MM-DD` in the tenant timezone. Derived on write; never from a server. */
  day: string;
}

export interface MetaRecord {
  key: string;
  value: unknown;
}

export interface YourstoreDB extends DBSchema {
  appointments: {
    key: string;
    value: MirroredAppointment;
    indexes: { 'by-day': string };
  };
  clients: {
    key: string;
    value: ClientDetail;
    indexes: { 'by-name': string };
  };
  services: { key: string; value: Service };
  resources: { key: string; value: Resource };
  blackouts: { key: string; value: Blackout };
  meta: { key: string; value: MetaRecord };
  outbox: {
    key: string;
    value: OutboxEntry;
    indexes: { 'by-seq': number };
  };
}

export type AppDb = IDBPDatabase<YourstoreDB>;

export const DB_NAME = 'yourstore-app';
export const DB_VERSION = 1;

/** Meta keys. Strings in one place, so a typo is a compile error. */
export const META = {
  /** The SSE resume cursor (`Last-Event-ID`). */
  cursor: 'cursor',
  /** When the mirror last heard from the server, as an ISO instant. */
  lastSyncAt: 'lastSyncAt',
  /** `GET /v1/session`, cached so the shell can paint offline. */
  session: 'session',
  /** `GET /v1/settings/hours`. */
  hours: 'hours',
  /** The `[from, to]` day range `appointments` is known to cover. */
  window: 'window',
  /** The next outbox sequence number — the total order intents replay in. */
  seq: 'seq',
} as const;

export interface MirrorWindow {
  from: string;
  to: string;
}

export async function openAppDb(name: string = DB_NAME): Promise<AppDb> {
  return openDB<YourstoreDB>(name, DB_VERSION, {
    upgrade(db) {
      const appointments = db.createObjectStore('appointments', { keyPath: 'appointment_id' });
      appointments.createIndex('by-day', 'day');

      const clients = db.createObjectStore('clients', { keyPath: 'client_id' });
      clients.createIndex('by-name', 'display_name');

      db.createObjectStore('services', { keyPath: 'service_id' });
      db.createObjectStore('resources', { keyPath: 'resource_id' });
      db.createObjectStore('blackouts', { keyPath: 'blackout_id' });
      db.createObjectStore('meta', { keyPath: 'key' });

      const outbox = db.createObjectStore('outbox', { keyPath: 'id' });
      outbox.createIndex('by-seq', 'seq');
    },
  });
}

export async function readMeta<T>(db: AppDb, key: string): Promise<T | undefined> {
  const row = await db.get('meta', key);
  return row === undefined ? undefined : (row.value as T);
}

export async function writeMeta(db: AppDb, key: string, value: unknown): Promise<void> {
  await db.put('meta', { key, value });
}

/**
 * Everything this device knows, gone.
 *
 * Used on sign-out, and it takes the outbox with it. That is the honest
 * behaviour: an unsent intent belongs to the person who typed it and to the
 * tenant they were signed into, and carrying it across a sign-out is how a
 * booking ends up replayed into somebody else's book — the exact write
 * `core-api`'s middleware refuses with a `403` and which should never have been
 * sent in the first place.
 */
export async function clearDevice(db: AppDb): Promise<void> {
  const stores = [
    'appointments',
    'clients',
    'services',
    'resources',
    'blackouts',
    'meta',
    'outbox',
  ] as const;
  const tx = db.transaction(stores, 'readwrite');
  await Promise.all(stores.map((store) => tx.objectStore(store).clear()));
  await tx.done;
}

export type { Session };
export type { Hours };
